import React, { useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

const homeFaqs = [
  {
    question: "What is the eligibility criteria for a loan?",
    answer:
      "You must be a salaried individual, aged between 21 and 60, employed for at least 6 months, and meet our minimum monthly salary requirements.",
  },
  {
    question: "How long does the approval process take?",
    answer:
      "Our automated system processes applications instantly. Approval usually takes a few minutes, and funds are disbursed within 5 minutes upon final confirmation.",
  },
  {
    question: "What documents do I need to provide?",
    answer:
      "You'll need your PAN card, Aadhaar card, salary slips for the last 3 months, and bank statements for the last 6 months.",
  },
  {
    question: "Is my personal data safe with you?",
    answer:
      "Yes, we use bank-grade encryption and secure servers to protect your data. We adhere to all regulatory privacy standards to ensure your information is safe.",
  },
];

const HomeFAQ: React.FC = () => {
  // Set the first FAQ item to be expanded by default (index 0)
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  return (
    <section className="w-full py-16 sm:py-20 md:py-24 px-6 lg:px-20 bg-white font-sans flex flex-col items-center">
      <div className="max-w-5xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Left Column: Heading, Subheading, and Still Have Questions Card (5 cols) */}
        <div className="lg:col-span-5 flex flex-col gap-6 w-full text-left">
          <div className="flex flex-col gap-3">
            <h2 className="text-3xl sm:text-4xl lg:text-[42px] font-black text-[#0F172A] leading-[1.1] tracking-tight text-center lg:text-left">
              <span className="block lg:whitespace-nowrap">
                FREQUENTLY ASKED
              </span>

              <span className="text-[var(--primary)] block mt-1">
                QUESTIONS.
              </span>
            </h2>
            <p className="text-slate-500 font-medium text-[14px] sm:text-base leading-relaxed mt-1">
              Can't find what you're looking for? Our support team is available
              24/7 to assist you.
            </p>
          </div>

          {/* Still Have Questions Helper Card */}
          <div className="bg-[#0B1220] rounded-[24px] p-6 sm:p-8 relative overflow-hidden flex flex-col justify-between min-h-[220px] sm:min-h-[240px] border border-white/[0.04] shadow-[0_10px_25px_rgba(11,30,63,0.15)]">
            {/* Faded "?" watermark in bottom right */}
            <div className="absolute right-[1px] bottom-[-4px] text-white/[0.03] text-[150px] font-black pointer-events-none select-none  z-0 leading-none">
             <svg width="82" height="79" viewBox="0 0 82 79" fill="none" xmlns="http://www.w3.org/2000/svg">
<g opacity="0.1">
<path d="M45 75C46.75 75 48.2292 74.3958 49.4375 73.1875C50.6458 71.9792 51.25 70.5 51.25 68.75C51.25 67 50.6458 65.5208 49.4375 64.3125C48.2292 63.1042 46.75 62.5 45 62.5C43.25 62.5 41.7708 63.1042 40.5625 64.3125C39.3542 65.5208 38.75 67 38.75 68.75C38.75 70.5 39.3542 71.9792 40.5625 73.1875C41.7708 74.3958 43.25 75 45 75ZM40.5 55.75H49.75C49.75 52.75 50.0833 50.5417 50.75 49.125C51.4167 47.7083 52.8333 45.9167 55 43.75C57.9167 40.8333 59.9792 38.3958 61.1875 36.4375C62.3958 34.4792 63 32.25 63 29.75C63 25.3333 61.5 21.7708 58.5 19.0625C55.5 16.3542 51.4583 15 46.375 15C41.7917 15 37.8958 16.125 34.6875 18.375C31.4792 20.625 29.25 23.75 28 27.75L36.25 31C36.8333 28.75 38 26.9375 39.75 25.5625C41.5 24.1875 43.5417 23.5 45.875 23.5C48.125 23.5 50 24.1042 51.5 25.3125C53 26.5208 53.75 28.125 53.75 30.125C53.75 31.5417 53.2917 33.0417 52.375 34.625C51.4583 36.2083 49.9167 37.9583 47.75 39.875C45 42.2917 43.1042 44.6042 42.0625 46.8125C41.0208 49.0208 40.5 52 40.5 55.75ZM10 90C7.25 90 4.89583 89.0208 2.9375 87.0625C0.979167 85.1042 0 82.75 0 80V10C0 7.25 0.979167 4.89583 2.9375 2.9375C4.89583 0.979167 7.25 0 10 0H80C82.75 0 85.1042 0.979167 87.0625 2.9375C89.0208 4.89583 90 7.25 90 10V80C90 82.75 89.0208 85.1042 87.0625 87.0625C85.1042 89.0208 82.75 90 80 90H10ZM10 80H80V10H10V80ZM10 10V80V10Z" fill="white"/>
</g>
</svg>

            </div>

            <div className="relative z-10 flex flex-col gap-2.5">
              <h3 className="text-[#41A655] font-black text-lg sm:text-[20px] tracking-tight">
                Still have questions?
              </h3>
              <p className="text-slate-400 font-medium text-[13px] sm:text-[14px] leading-relaxed">
                Our team of experts is available around the clock to help you.
              </p>
            </div>

            <div className="relative z-10 mt-6">
              <a
                href="/contact"
                className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-[#0F172A] font-extrabold text-[12px] uppercase tracking-wider py-3 px-5 rounded-xl transition-all active:scale-95 shadow-sm"
              >
                <span>Contact Support</span>
                <svg
                  width="20"
                  height="18"
                  viewBox="0 0 20 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 18V16H17V8.9C17 6.95 16.3208 5.29583 14.9625 3.9375C13.6042 2.57917 11.95 1.9 10 1.9C8.05 1.9 6.39583 2.57917 5.0375 3.9375C3.67917 5.29583 3 6.95 3 8.9V15H2C1.45 15 0.979167 14.8042 0.5875 14.4125C0.195833 14.0208 0 13.55 0 13V11C0 10.65 0.0875 10.3208 0.2625 10.0125C0.4375 9.70417 0.683333 9.45833 1 9.275L1.075 7.95C1.20833 6.81667 1.5375 5.76667 2.0625 4.8C2.5875 3.83333 3.24583 2.99167 4.0375 2.275C4.82917 1.55833 5.7375 1 6.7625 0.6C7.7875 0.2 8.86667 0 10 0C11.1333 0 12.2083 0.2 13.225 0.6C14.2417 1 15.15 1.55417 15.95 2.2625C16.75 2.97083 17.4083 3.80833 17.925 4.775C18.4417 5.74167 18.775 6.79167 18.925 7.925L19 9.225C19.3167 9.375 19.5625 9.6 19.7375 9.9C19.9125 10.2 20 10.5167 20 10.85V13.15C20 13.4833 19.9125 13.8 19.7375 14.1C19.5625 14.4 19.3167 14.625 19 14.775V16C19 16.55 18.8042 17.0208 18.4125 17.4125C18.0208 17.8042 17.55 18 17 18H9ZM7 11C6.71667 11 6.47917 10.9042 6.2875 10.7125C6.09583 10.5208 6 10.2833 6 10C6 9.71667 6.09583 9.47917 6.2875 9.2875C6.47917 9.09583 6.71667 9 7 9C7.28333 9 7.52083 9.09583 7.7125 9.2875C7.90417 9.47917 8 9.71667 8 10C8 10.2833 7.90417 10.5208 7.7125 10.7125C7.52083 10.9042 7.28333 11 7 11ZM13 11C12.7167 11 12.4792 10.9042 12.2875 10.7125C12.0958 10.5208 12 10.2833 12 10C12 9.71667 12.0958 9.47917 12.2875 9.2875C12.4792 9.09583 12.7167 9 13 9C13.2833 9 13.5208 9.09583 13.7125 9.2875C13.9042 9.47917 14 9.71667 14 10C14 10.2833 13.9042 10.5208 13.7125 10.7125C13.5208 10.9042 13.2833 11 13 11ZM4.025 9.45C3.90833 7.68333 4.44167 6.16667 5.625 4.9C6.80833 3.63333 8.28333 3 10.05 3C11.5333 3 12.8375 3.47083 13.9625 4.4125C15.0875 5.35417 15.7667 6.55833 16 8.025C14.4833 8.00833 13.0875 7.6 11.8125 6.8C10.5375 6 9.55833 4.91667 8.875 3.55C8.60833 4.88333 8.04583 6.07083 7.1875 7.1125C6.32917 8.15417 5.275 8.93333 4.025 9.45Z"
                    fill="#131B2E"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Right Column: Flat accordion list of FAQs (7 cols) */}
        <div className="lg:col-span-7 flex flex-col w-full">
          {homeFaqs.map((faq, idx) => {
            const isOpen = activeIndex === idx;
            return (
              <div
                key={idx}
                className="border-b border-slate-100 py-5 sm:py-6 transition-all duration-300"
              >
                <button
                  onClick={() => setActiveIndex(isOpen ? null : idx)}
                  className="w-full flex justify-between items-center text-left group"
                >
                  <span
                    className={`font-black text-[16px] sm:text-[18px] tracking-tight transition-colors duration-300 pr-6 ${
                      isOpen ? "text-[var(--primary)]" : "text-[#0F172A]"
                    }`}
                  >
                    {faq.question}
                  </span>
                  <div
                    className={`shrink-0 text-slate-400 group-hover:text-[#0F172A] transition-colors duration-300`}
                  >
                    {isOpen ? (
                      <FaChevronUp className="text-[13px] sm:text-[14px]" />
                    ) : (
                      <FaChevronDown className="text-[13px] sm:text-[14px]" />
                    )}
                  </div>
                </button>

                {/* Answer Accordion panel with smooth transition */}
                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    isOpen
                      ? "grid-rows-[1fr] opacity-100 mt-3 sm:mt-4"
                      : "grid-rows-[0fr] opacity-0 pointer-events-none"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="text-slate-500 font-medium text-[14px] sm:text-[15px] leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HomeFAQ;
